/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppState, DisabledPrevButtonType } from './appTypes';
import { Payload, TimeSlots } from '../../services/Appointment/model';
import { services } from '../../context';
import { SlotsObjects } from '../timeslots/timeSlotsTypes';

const initialState: AppState = {
  isLoading: true,
  disabledPrevButton: '',
  operation: '',
  booking: {
    orderNo: '',
    questionsToken: '',
    payOrderLink: '',
    payOrderLinkEnabled: true,
  },
  selected: {
    serviceId: '',
    storeId: '',
    timeSlot: null,
  },
};

const timeSlotExtractor = (
  timeSlots: SlotsObjects,
  timeSlotId: string,
  storeId: string,
): TimeSlots | undefined => {
  if (!timeSlotId) return undefined;

  const allTimeSlotsOnGivenDate = timeSlots[storeId].find((slots) =>
    slots.timeSlots.some((slot) => slot.id === timeSlotId),
  );
  return allTimeSlotsOnGivenDate?.timeSlots.find(
    (slot) => slot.id === timeSlotId,
  );
};

export const confirmAppointment = createAsyncThunk(
  'app/confirmAppointment',
  async (
    {
      zipCode,
      serviceId,
      storeId,
      timeSlotId,
      from,
      serviceCode,
      payload,
    }: {
      zipCode: string;
      serviceId: string;
      storeId: string;
      timeSlotId: string;
      from: string;
      serviceCode: string;
      payload: Payload;
    },
    { rejectWithValue },
  ) => {
    const { result, error } = await services.appointment.confirmAppointment(
      zipCode,
      serviceId,
      storeId,
      timeSlotId,
      from,
      serviceCode,
      payload,
    );

    if (error) {
      return rejectWithValue(error);
    }

    return {
      orderNo: result.orderNo as string,
      payOrderLink: result.payOrderLink as string,
      questionsToken: result.questionsToken as string,
      payOrderLinkEnabled: result.payOrderLinkEnabled as boolean,
    };
  },
);

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    updateLoadingState(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    updateSelectedService(state, action: PayloadAction<string>) {
      state.selected.serviceId = action.payload;
      state.selected.storeId = 'default';
      state.selected.timeSlot = null;
    },
    updateSelectedStore(state, action: PayloadAction<string>) {
      state.selected.storeId = action.payload;
      state.selected.timeSlot = null;
    },
    updateSelectedTimeSlot(
      state,
      action: PayloadAction<{
        timeSlots: SlotsObjects;
        id: string;
        storeId: string;
      }>,
    ) {
      state.selected.timeSlot =
        timeSlotExtractor(
          action.payload.timeSlots,
          action.payload.id,
          action.payload.storeId,
        ) || null;
    },
    setOperation(state, action: PayloadAction<string>) {
      state.operation = action.payload;
    },
    setDisabledPrevButton(
      state,
      action: PayloadAction<DisabledPrevButtonType>,
    ) {
      state.disabledPrevButton = action.payload;
    },
    updateAppStateFromCache(state, action: PayloadAction<AppState>) {
      return action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(confirmAppointment.fulfilled, (state, action) => {
      state.booking.orderNo = action.payload.orderNo;
      state.booking.payOrderLink = action.payload.payOrderLink;
      state.booking.questionsToken = action.payload.questionsToken;
      state.booking.payOrderLinkEnabled = action.payload.payOrderLinkEnabled;
    });
  },
});

export const {
  updateLoadingState,
  updateSelectedService,
  updateSelectedStore,
  updateSelectedTimeSlot,
  setOperation,
  setDisabledPrevButton,
  updateAppStateFromCache,
} = appSlice.actions;

export default appSlice.reducer;
